import { useEffect, useRef } from 'react';
import { useAccessibilitySettings } from '../contexts/AccessibilityContext';

interface ShortcutMap {
  [key: string]: () => void;
}

/** How long a latched modifier waits for the key it modifies, in milliseconds. */
const STICKY_MODIFIER_TIMEOUT_MS = 5000;

const MODIFIER_KEYS: Record<string, 'Ctrl' | 'Shift' | 'Alt'> = {
  Control: 'Ctrl',
  Meta: 'Ctrl',
  Shift: 'Shift',
  Alt: 'Alt',
};

/**
 * The listener behind the global shortcut chords (`GLOBAL_SHORTCUTS` in
 * `accessibilityConfig`) and the shortcuts overlay.
 *
 * S132 fixed two defects here, both of which only showed up against a keystroke
 * a real keyboard can produce:
 *
 *   - **`?` could not be typed.** `?` is Shift+/ on every US layout, so the
 *     event arrives with `shiftKey` set. The old code built the combination
 *     `'Shift+?'` — no such entry — and its fallback branch for a bare `'?'`
 *     required `!event.shiftKey`, so it declined as well. The config's own
 *     description ("Press ? for keyboard shortcuts"), the overlay's button
 *     tooltip, and the dialog's only keyboard opener were all dead. The suite
 *     passed because it dispatched `'?'` with no modifiers, which no physical
 *     keyboard produces.
 *   - **A bare `?` ran its handler TWICE**, matched by the combination lookup
 *     and then again by the fallback. The overlay survived only because its
 *     handler was `() => setIsOpen(!isOpen)` and both calls closed over the same
 *     stale `isOpen`; written the way React documents it — `setIsOpen((o) => !o)`
 *     — the second call would have undone the first and the dialog would never
 *     have appeared. A trap for whoever touched that line next.
 *
 * Shift is now dropped from the combination for any printable character, since
 * the shifted character is already in `event.key` (`?`, `:`, `_`), and the
 * fallback branch is gone.
 */
export function useKeyboardShortcuts(shortcuts: ShortcutMap): void {
  // The map is almost always rebuilt on every render by the calling component
  // (an object literal in the body). Depending on it directly would tear the
  // listener down and re-add it on every render; a ref keeps one listener for
  // the life of the component while still calling the latest handlers.
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  /**
   * S133: "Sticky Keys" was a checkbox that wrote a Firestore document and
   * nothing else — no file in src/ other than the panel mentioned the setting.
   * The help text promised "press modifier keys one at a time", which is exactly
   * what a child who types with one hand, a head pointer or a mouth stick needs
   * in order to reach any chord at all, and it did nothing.
   *
   * The operating system's own sticky keys are outside a web page's reach, so
   * this latches modifiers for the app's OWN chords: press Alt, let go, then
   * press D, and Alt+D fires. The latch clears when it is used, when it times
   * out, and on Escape.
   */
  const stickyEnabled = useAccessibilitySettings().stickyKeys;
  const stickyRef = useRef<{ modifiers: Set<'Ctrl' | 'Shift' | 'Alt'>; expiresAt: number }>({
    modifiers: new Set(),
    expiresAt: 0,
  });

  // Dropping the setting mid-session must not leave a modifier latched.
  useEffect(() => {
    if (!stickyEnabled) stickyRef.current = { modifiers: new Set(), expiresAt: 0 };
  }, [stickyEnabled]);

  const stickyEnabledRef = useRef(stickyEnabled);
  stickyEnabledRef.current = stickyEnabled;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input
      const target = event.target as HTMLElement;
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true';

      if (isInput && event.key !== 'Escape') {
        return;
      }

      const sticky = stickyRef.current;
      const stickyOn = stickyEnabledRef.current;

      if (stickyOn && sticky.expiresAt && Date.now() > sticky.expiresAt) {
        sticky.modifiers.clear();
        sticky.expiresAt = 0;
      }

      const modifierName = MODIFIER_KEYS[event.key];
      if (stickyOn && modifierName) {
        // Pressing a latched modifier again releases it, so a mis-press is
        // undoable without waiting out the timeout.
        if (sticky.modifiers.has(modifierName)) {
          sticky.modifiers.delete(modifierName);
        } else {
          sticky.modifiers.add(modifierName);
        }
        sticky.expiresAt = sticky.modifiers.size ? Date.now() + STICKY_MODIFIER_TIMEOUT_MS : 0;
        return;
      }

      if (stickyOn && event.key === 'Escape' && sticky.modifiers.size) {
        sticky.modifiers.clear();
        sticky.expiresAt = 0;
        // Fall through: Escape is itself a shortcut ('close-modal').
      }

      const latched = stickyOn ? sticky.modifiers : new Set<'Ctrl' | 'Shift' | 'Alt'>();
      if (stickyOn && latched.size) {
        sticky.modifiers = new Set();
        sticky.expiresAt = 0;
      }

      // Build the key combination string
      const keys: string[] = [];

      if (event.ctrlKey || event.metaKey || latched.has('Ctrl')) {
        keys.push('Ctrl');
      }

      // A shifted SYMBOL already carries the shift in `event.key` — the US layout
      // reports Shift+/ as '?', not as Shift plus '/'. Naming Shift as well
      // would demand the impossible chord 'Shift+?'. Letters are the exception:
      // Shift+p reports 'P', which is indistinguishable from an upper-cased 'p'
      // once the chord is normalised, so 'Ctrl+Shift+Z' must keep its Shift.
      // Named keys (Enter, Tab, ArrowUp) are longer than one character and keep
      // it too.
      const isShiftedSymbol = event.key.length === 1 && !/^[a-z]$/i.test(event.key);
      if ((event.shiftKey || latched.has('Shift')) && !isShiftedSymbol) {
        keys.push('Shift');
      }
      if (event.altKey || latched.has('Alt')) {
        keys.push('Alt');
      }

      // A letter is upper-cased so 'Ctrl+S' matches whether or not caps lock is
      // on; a symbol is left exactly as typed.
      const keyName = /^[a-z]$/i.test(event.key) ? event.key.toUpperCase() : event.key;

      keys.push(keyName);
      const combination = keys.join('+');

      const handler = shortcutsRef.current[combination];
      if (handler) {
        event.preventDefault();
        handler();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
}
